import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { expensesAPI } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { isAdmin as roleIsAdmin, isAccountant } from "../lib/roles";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Receipt, Plus, MagnifyingGlass, Trash, PencilSimple, PaperPlaneRight, CreditCard, Eye } from "@phosphor-icons/react";
import ReceiptPreview, { ReceiptBadge } from "../components/ReceiptPreview";
import { formatDate } from "../lib/formatDate";
import { formatCurrency } from "../lib/formatCurrency";
import { toast } from "sonner";
import Comments from "../components/Comments";
import { QuickFilters } from "../components/QuickFilters";
import { CodingCell } from "../components/CodingCell";
import { AutopilotBadge } from "../components/AutopilotBadge";
import { MakeAutopilotRuleButton } from "../components/MakeAutopilotRuleButton";
import { AttachReceiptButton } from "../components/AttachReceiptButton";

const statusStyle = (s) => ({
  draft: "bg-zinc-100 text-zinc-500 border-zinc-200",
  submitted: "bg-amber-100 text-amber-700 border-amber-200",
  approved: "bg-emerald-100 text-emerald-700 border-emerald-200",
  rejected: "bg-red-100 text-red-700 border-red-200",
}[s] || "bg-zinc-100 text-zinc-500 border-zinc-200");

export default function ExpensesPage() {
  const location = useLocation();
  const initialState = location.state || {};
  const [expenses, setExpenses] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState(initialState.statusFilter || "all");
  const [typeFilter, setTypeFilter] = useState(initialState.typeFilter || "all");
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState(() => {
    const f = {};
    ["submitted_by", "vendor", "job_id", "gl_account_id", "card_last_four"].forEach((k) => {
      if (initialState[k]) f[k] = initialState[k];
    });
    return f;
  });
  const [selected, setSelected] = useState(null);
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = roleIsAdmin(user);
  const readOnly = isAccountant(user);

  const handleAdminDelete = async (id, e) => {
    e?.stopPropagation();
    // Loud confirm — admin cascade-delete touches QBO + JT
    if (!window.confirm("Delete this expense? If it was pushed, the QBO Purchase and JobTread Document will also be deleted. Any linked Mercury swipe will be returned to the queue. This cannot be undone.")) return;
    try {
      const { data } = await expensesAPI.delete(id);
      const warnings = data?.cleanup_errors?.length ? ` Cleanup warnings: ${data.cleanup_errors.join("; ")}` : "";
      toast.success(`Deleted.${warnings}`);
      fetchExpenses();
      setSelected(null);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const fetchExpenses = async () => {
    try {
      const params = {};
      if (statusFilter !== "all") params.status = statusFilter;
      if (typeFilter !== "all") params.expense_type = typeFilter;
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const { data } = await expensesAPI.list(params);
      setExpenses(data.expenses);
      setTotal(data.total);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchExpenses(); }, [statusFilter, typeFilter, filters]); // eslint-disable-line react-hooks/exhaustive-deps

  // Deep link from notifications: /expenses?open=<id>
  useEffect(() => {
    const openId = new URLSearchParams(location.search).get("open");
    if (!openId) return;
    expensesAPI.get(openId).then(({ data }) => setSelected(data)).catch(() => toast.error("Expense not found", { id: "open-expense-missing" }));
  }, [location.search]);

  const closeDetail = () => {
    setSelected(null);
    if (new URLSearchParams(location.search).has("open")) navigate("/expenses", { replace: true });
  };

  const handleReceiptAttached = (updated) => {
    setSelected(updated);
    setExpenses((prev) => prev.map((e) => (e.id === updated.id ? { ...e, ...updated } : e)));
  };

  // Auto-refresh every 60s so newly-submitted or auto-approved expenses appear without a manual reload.
  useEffect(() => {
    const id = setInterval(() => { fetchExpenses(); }, 60000);
    return () => clearInterval(id);
  }, [statusFilter, typeFilter, filters]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = async (id, e) => {
    e?.stopPropagation();
    try { await expensesAPI.submit(id); toast.success("Submitted"); fetchExpenses(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const handleDelete = async (id, e) => {
    e?.stopPropagation();
    try { await expensesAPI.delete(id); toast.success("Deleted"); fetchExpenses(); setSelected(null); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const filteredExpenses = expenses.filter((exp) =>
    !search || exp.vendor?.toLowerCase().includes(search.toLowerCase()) ||
    exp.submitted_by_name?.toLowerCase().includes(search.toLowerCase()) ||
    exp.job_name?.toLowerCase().includes(search.toLowerCase()) ||
    exp.gl_account_name?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="p-4 sm:p-6 lg:p-8" data-testid="expenses-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-5">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-zinc-900" style={{ fontFamily: 'DM Sans, sans-serif' }}>Expenses</h1>
          <p className="text-sm text-zinc-500 mt-0.5">{total} total</p>
        </div>
        <Button data-testid="new-expense-btn" onClick={() => navigate("/expenses/new")}
          disabled={readOnly}
          hidden={readOnly}
          className={`bg-[#002FA7] hover:bg-[#002280] text-white rounded-sm text-sm gap-1.5 shadow-sm hover:-translate-y-[1px] hover:shadow-md transition-all ${readOnly ? "hidden" : ""}`}>
          <Plus size={14} weight="bold" /> New Expense
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-col gap-2 mb-4">
        <div className="flex flex-col sm:flex-row gap-2">
          <div className="relative flex-1">
            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <Input data-testid="expense-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search vendor, submitter, job..." className="pl-8 rounded-sm h-8 text-sm" />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[180px] rounded-sm h-8 text-xs" data-testid="status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="draft,rejected">Pending Submission</SelectItem>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="submitted">Submitted</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
            </SelectContent>
          </Select>
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="w-[120px] rounded-sm h-8 text-xs" data-testid="type-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="job">Job</SelectItem>
              <SelectItem value="gl">Overhead Account</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <QuickFilters value={filters} onChange={setFilters} show={["submitter", "vendor", "job", "gl", "card"]} />
      </div>

      {/* Compact expense list */}
      <div className="bg-white border border-zinc-200 rounded-sm overflow-hidden">
        {loading ? (
          <div className="p-6 text-center animate-pulse text-zinc-400 text-sm">Loading...</div>
        ) : filteredExpenses.length > 0 ? (
          <div className="divide-y divide-zinc-100">
            <div className="hidden sm:grid sm:grid-cols-12 gap-3 px-4 py-2.5 bg-zinc-50 text-[10px] uppercase tracking-[0.15em] font-bold text-zinc-400">
              <div className="col-span-1">Date</div>
              <div className="col-span-1">Status</div>
              <div className="col-span-2">Vendor</div>
              <div className="col-span-2">Submitted By</div>
              <div className="col-span-4">Coding</div>
              <div className="col-span-1 text-right">Amount</div>
              <div className="col-span-1 text-right">Action</div>
            </div>
            {filteredExpenses.map((exp) => (
              <div
                key={exp.id}
                onClick={() => setSelected(exp)}
                className="px-4 py-3 sm:grid sm:grid-cols-12 sm:gap-3 sm:items-center hover:bg-zinc-50 transition-colors cursor-pointer"
                data-testid={`expense-row-${exp.id}`}
              >
                {/* Date */}
                <div className="col-span-1" data-testid={`expense-dates-${exp.id}`}>
                  <p className="text-sm text-zinc-800 font-medium">{formatDate(exp.transaction_date || exp.date)}</p>
                  <p className="text-[10px] text-zinc-400 mt-0.5">
                    {exp.post_date
                      ? `Posted ${formatDate(exp.post_date)}`
                      : !exp.mercury_transaction_id
                        ? <span className="inline-flex items-center px-1 py-0.5 rounded-sm bg-indigo-50 text-indigo-700 border border-indigo-200 text-[9px] uppercase tracking-wider font-bold" title="Manually entered — no bank transaction" data-testid={`expense-manual-entry-${exp.id}`}>Manual Entry</span>
                        : <span className="text-zinc-300">Not posted</span>}
                  </p>
                </div>
                {/* Status */}
                <div className="col-span-1 mt-1 sm:mt-0">
                  <Badge className={`text-[9px] uppercase tracking-wider rounded-sm ${statusStyle(exp.status)}`}>{exp.status}</Badge>
                </div>
                {/* Vendor */}
                <div className="col-span-2 mt-1 sm:mt-0 flex items-center gap-1.5 min-w-0">
                  {exp.is_simulated && (
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded-sm bg-amber-500 text-white text-[9px] uppercase tracking-[0.15em] font-bold flex-shrink-0" title="Training transaction — not pushed to QBO/JT">TEST</span>
                  )}
                  <AutopilotBadge exp={exp} />
                  <span className="text-sm font-medium text-zinc-800 truncate">{exp.vendor}</span>
                  {exp.mercury_transaction_id && <CreditCard size={11} className="text-zinc-300 flex-shrink-0" title="Linked to card transaction" />}
                  <ReceiptBadge receiptPath={exp.receipt_path} />
                </div>
                {/* Submitted by */}
                <div className="col-span-2 mt-1 sm:mt-0">
                  <p className="text-sm text-zinc-600 truncate">{exp.submitted_by_name || "—"}</p>
                </div>
                {/* Coding */}
                <div className="col-span-4 mt-1 sm:mt-0 min-w-0">
                  <CodingCell exp={exp} />
                </div>
                {/* Amount */}
                <div className="col-span-1 text-right mt-1 sm:mt-0">
                  <div className="flex items-center justify-end gap-1.5 flex-wrap">
                    {typeof exp.amount === "number" && exp.amount < 0 && (
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded-sm bg-emerald-600 text-white text-[9px] uppercase tracking-[0.15em] font-bold flex-shrink-0" title="Refund — pushes to QBO/JT as a credit">REFUND</span>
                    )}
                    <span className={`text-sm font-bold font-mono ${typeof exp.amount === "number" && exp.amount < 0 ? "text-emerald-700" : "text-zinc-900"}`}>{formatCurrency(exp.amount)}</span>
                  </div>
                </div>
                {/* Action */}
                <div className="col-span-1 flex justify-end gap-0.5 mt-1 sm:mt-0">
                  {/* Both draft AND rejected are user-editable. Rejected
                      expenses need an obvious path back to the edit form
                      so the submitter can correct what the approver flagged
                      and resubmit. */}
                  {!readOnly && (exp.status === "draft" || exp.status === "rejected") ? (
                    <>
                      <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); navigate(`/expenses/${exp.id}/edit`); }} data-testid={`edit-expense-${exp.id}`} className="h-7 w-7 p-0 text-zinc-400 hover:text-zinc-600" title={exp.status === "rejected" ? "Edit & resubmit" : "Edit draft"}>
                        <PencilSimple size={12} />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={(e) => handleSubmit(exp.id, e)} data-testid={`submit-expense-${exp.id}`} className="h-7 w-7 p-0 text-[#002FA7] hover:text-[#002280]" title={exp.status === "rejected" ? "Resubmit for approval" : "Submit for approval"}>
                        <PaperPlaneRight size={12} />
                      </Button>
                      {/* Only let the user hard-delete their own DRAFTS. Rejected
                          expenses preserve the audit trail (use admin delete if
                          a full purge is needed). */}
                      {exp.status === "draft" && (
                        <Button size="sm" variant="ghost" onClick={(e) => handleDelete(exp.id, e)} data-testid={`delete-expense-${exp.id}`} className="h-7 w-7 p-0 text-red-400 hover:text-red-600 hover:bg-red-50" title="Delete draft">
                          <Trash size={12} />
                        </Button>
                      )}
                    </>
                  ) : null}
                  {!readOnly && isAdmin && exp.status !== "draft" && (
                    <Button size="sm" variant="ghost" onClick={(e) => handleAdminDelete(exp.id, e)} data-testid={`admin-delete-expense-${exp.id}`} className="h-7 w-7 p-0 text-red-400 hover:text-red-600 hover:bg-red-50" title="Admin delete (cascades to QBO + JT)">
                      <Trash size={12} />
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="p-10 text-center">
            <Receipt size={32} weight="duotone" className="text-zinc-200 mx-auto mb-2" />
            <p className="text-sm text-zinc-400 mb-3">No expenses found</p>
            {!readOnly && (
              <Button onClick={() => navigate("/expenses/new")} className="bg-[#002FA7] hover:bg-[#002280] text-white rounded-sm text-sm">Create Expense</Button>
            )}
          </div>
        )}
      </div>

      {/* Expense Detail Dialog */}
      <Dialog open={!!selected} onOpenChange={closeDetail}>
        <DialogContent className="rounded-sm max-w-lg" data-testid="expense-detail-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold tracking-tight" style={{ fontFamily: 'DM Sans, sans-serif' }}>Expense Detail</DialogTitle>
          </DialogHeader>
          {selected && (
            <div className="space-y-4 mt-1">
              {/* Header */}
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-base font-semibold text-zinc-900">{selected.vendor}</p>
                  <p className="text-xs text-zinc-400 mt-0.5">{selected.submitted_by_name || "Employee"} &middot; Txn {formatDate(selected.transaction_date || selected.date)} &middot; {selected.post_date
                    ? <>Posted {formatDate(selected.post_date)}</>
                    : !selected.mercury_transaction_id
                      ? <span className="inline-flex items-center px-1 py-0.5 rounded-sm bg-indigo-50 text-indigo-700 border border-indigo-200 text-[9px] uppercase tracking-wider font-bold">Manual Entry</span>
                      : <>Posted <span className="italic">pending</span></>}</p>
                </div>
                <div className="text-right">
                  <p className="text-lg font-bold text-zinc-900 font-mono">{formatCurrency(selected.amount)}</p>
                  <Badge className={`text-[9px] uppercase rounded-sm ${statusStyle(selected.status)}`}>{selected.status}</Badge>
                </div>
              </div>

              {/* Rejection reason banner — shown when the approver bounced
                  this expense back. Matches the same banner that's on the
                  New Expense edit form so the submitter sees the reason
                  whether they open the detail dialog or jump straight into
                  Edit. */}
              {selected.status === "rejected" && (
                <div className="bg-red-50 border border-red-200 rounded-sm p-3" data-testid="detail-rejection-banner">
                  <p className="text-[10px] uppercase tracking-widest text-red-700 font-bold">
                    Rejected{selected.rejected_by_name ? ` by ${selected.rejected_by_name}` : ""}
                  </p>
                  <p className="text-sm text-red-800 mt-1 leading-snug">
                    {selected.rejection_reason || "No reason was provided. Reach out to the approver if you're unsure what to change."}
                  </p>
                  <p className="text-[10px] text-red-700/70 mt-1.5">
                    Edit and Resubmit using the buttons below.
                  </p>
                </div>
              )}

              {/* Description */}
              {selected.description && (
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">Transaction Description</p>
                  <p className="text-sm text-zinc-700 mt-0.5">{selected.description}</p>
                </div>
              )}

              {/* Coding / Splits */}
              <div>
                <p className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold mb-1.5">Allocation</p>
                {selected.splits?.length > 0 ? (
                  <div className="space-y-1.5">
                    {selected.splits.map((s, i) => (
                      <div key={s._uid || `${selected.id}-split-${i}`} className="flex items-center justify-between bg-zinc-50 rounded-sm px-3 py-2">
                        <div className="min-w-0">
                          <p className="text-sm text-zinc-800 font-medium truncate">
                            {s.type === "job" ? (s.job_name || s.job_id || "Job") : (s.gl_account_name || s.gl_account_id || "Overhead")}
                          </p>
                          {s.budget_item_name && (
                            <p className="text-[11px] text-zinc-400 truncate">
                              {s.budget_item_name}
                              {s.unit_of_measure && <span className="ml-1 font-mono">({s.unit_of_measure})</span>}
                              {s.quantity && <span className="ml-1">x{s.quantity}</span>}
                            </p>
                          )}
                          {s.budget_item_group && <p className="text-[10px] text-zinc-300 truncate">{s.budget_item_group}</p>}
                        </div>
                        <span className="text-sm font-bold font-mono text-zinc-700 flex-shrink-0 ml-3">{formatCurrency(s.amount)}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="bg-zinc-50 rounded-sm px-3 py-2">
                    <p className="text-sm text-zinc-800 font-medium">
                      {selected.expense_type === "job" ? (selected.job_name || "Job") : (selected.gl_account_name || "Overhead Account")}
                    </p>
                    {selected.budget_item_name && (
                      <p className="text-[11px] text-zinc-400">{selected.budget_item_name}</p>
                    )}
                  </div>
                )}
              </div>

              {/* Receipt */}
              {selected.receipt_path ? (
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold mb-1.5">Receipt</p>
                  <ReceiptPreview receiptPath={selected.receipt_path} size="md" />
                </div>
              ) : (
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold mb-1.5">Receipt</p>
                  <p className="text-xs text-zinc-400 italic">No receipt attached</p>
                  {!readOnly && selected.status === "approved" && (isAdmin || selected.submitted_by === user?.id) && (
                    <div className="mt-2"><AttachReceiptButton exp={selected} onAttached={handleReceiptAttached} /></div>
                  )}
                </div>
              )}

              {/* Comments */}
              <Comments entityType="expense" entityId={selected.id} />

              {/* Actions */}
              <div className="flex gap-2 pt-2 border-t border-zinc-100">
                {!readOnly && (selected.status === "draft" || selected.status === "rejected") && (
                  <Button size="sm" variant="outline" onClick={() => { setSelected(null); navigate(`/expenses/${selected.id}/edit`); }} data-testid="detail-edit-btn" className="rounded-sm text-xs gap-1">
                    <PencilSimple size={12} /> Edit
                  </Button>
                )}
                {!readOnly && selected.status === "rejected" && (
                  <Button size="sm" onClick={(e) => { handleSubmit(selected.id, e); setSelected(null); }} data-testid="detail-resubmit-btn" className="bg-[#002FA7] hover:bg-[#002280] text-white rounded-sm text-xs gap-1">
                    <PaperPlaneRight size={12} /> Resubmit for Approval
                  </Button>
                )}
                {!readOnly && selected.status === "draft" && (
                  <>
                    <Button size="sm" onClick={(e) => { handleSubmit(selected.id, e); setSelected(null); }} data-testid="detail-submit-btn" className="bg-[#002FA7] hover:bg-[#002280] text-white rounded-sm text-xs gap-1">
                      <PaperPlaneRight size={12} /> Submit
                    </Button>
                    <Button size="sm" variant="destructive" onClick={(e) => handleDelete(selected.id, e)} data-testid="detail-delete-btn" className="rounded-sm text-xs gap-1">
                      <Trash size={12} /> Delete
                    </Button>
                  </>
                )}
                {!readOnly && isAdmin && <MakeAutopilotRuleButton exp={selected} />}
                {!readOnly && isAdmin && selected.status !== "draft" && (
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={(e) => handleAdminDelete(selected.id, e)}
                    data-testid="detail-admin-delete-btn"
                    className="rounded-sm text-xs gap-1 ml-auto"
                    title="Cascades to QBO + JobTread + unlinks the Mercury swipe"
                  >
                    <Trash size={12} /> Admin Delete
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
